import React, { useState, useEffect, useCallback, useMemo } from "react";

import { makeStyles } from "@mui/styles";
import { Grid, Button, Box, Paper, Typography, TextField, InputAdornment, IconButton as MuiIconButton, Chip, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import {
  NewCategory,
  Category,
  TargetBox,
  SourceBox,
  NewGroup,
  Group,
} from "./components";
import { useSelector, useDispatch } from "react-redux";
import {
  toggleNewCategory,
  getMenuCategories,
  toggleNewGroup,
  getMenuGroups,
  deleteMenuGroup,
  deleteMenuCategory,
  updateCategories,
  updateAllCategories,
} from "store/actions/restaurantMenu";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from "immutability-helper";

import { BackButton } from "components";
import { Link as RouterLink } from "react-router-dom";
import useOffShelfDishes from "hooks/useOffShelfDishes";
import { countOffShelf } from "util/offShelf";
import { hasVisibleItems } from "util/menuFilters";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    margin: theme.spacing(2),
    backgroundColor: "#f7f7f7",
    display: "flex",
    flexGrow: 1,

    flexDirection: "column",
    flexShrink: 0,
    minWidth: 0,
    // The shared TextField theme has a 300px minimum, wider than a tablet column.
    "& .MuiTextField-root": {
      minWidth: 0,
    },
    [theme.breakpoints.up("sm")]: {
      // Main's toolbar is 64px tall; leave room for this page's margins.
      height: `calc(100dvh - 64px - ${theme.spacing(4)})`,
      minHeight: 400,
      flexGrow: 0,
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      margin: theme.spacing(1),
    },
  },
  panels: {
    flex: 1,
    minHeight: 0,
  },
  panel: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
    [theme.breakpoints.up("sm")]: {
      height: "100%",
    },
  },
  panelScroll: {
    minHeight: 0,
    overflowY: "auto",
    overscrollBehaviorY: "contain",
    [theme.breakpoints.up("sm")]: {
      flex: 1,
    },
    [theme.breakpoints.down("sm")]: {
      maxHeight: "60dvh",
    },
  },
  content: {
    padding: 0,
  },
  inner: {
    minWidth: 1050,
  },
  flexSection: {
    display: "flex",
    flexDirection: "row",
  },

  flexColScroll: {
    minHeight: "100%",
  },

  flexNoShrink: {
    flexShrink: 0,
  },
  leftContainer: {
    padding: theme.spacing(1),
    margin: theme.spacing(1),
    backgroundColor: "#f7f7f7",
    flex: 3,
    flexDirection: "column",
    alignItems: "flex-start",
  },
  rightContainer: {
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(2),
    },
  },
  rightContent: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    flex: 1,
  },
  avatar: {
    marginRight: theme.spacing(2),
  },
  actions: {
    justifyContent: "flex-end",
  },
  textLink: {
    textDecoration: "underline",
  },
}));

const MenuCategroy = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const openNewCategory = () => dispatch(toggleNewCategory());
  const loadMenuCategories = (menuId) => dispatch(getMenuCategories(menuId));
  const loadMenuGroups = (menuId) => dispatch(getMenuGroups(menuId));
  const openNewGroup = () => dispatch(toggleNewGroup());
  const deleteGroup = (menuId, groupId) =>
    dispatch(deleteMenuGroup(menuId, groupId));
  const deleteCategory = (menuId, categoryId) =>
    dispatch(deleteMenuCategory(menuId, categoryId));
  const updateNewCategories = (newCategories) =>
    dispatch(updateCategories(newCategories));

  const updateTheAllCategories = (allCategories) =>
    dispatch(updateAllCategories(allCategories));

  const showNewCategory = useSelector(
    (state) => state.restaurantMenu.showNewCategory
  );
  const showNewGroup = useSelector(
    (state) => state.restaurantMenu.showNewGroup
  );
  const categories = useSelector(
    (state) => state.restaurantMenu.menuCategories
  );
  const groups = useSelector((state) => state.restaurantMenu.menuGroups);
  const user = useSelector((state) => state.account.user);
  const isAdmin = user && user.role === "admin";
  const [categoryList, setCategoryList] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [dishSearch, setDishSearch] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [showIds, setShowIds] = useState(false);
  const [offShelfOnly, setOffShelfOnly] = useState(false);
  const menuId = props.match.params.menuId;
  const dishCount = categoryList.reduce(
    (count, category) => count + (category.items ? category.items.length : 0),
    0
  );

  // The live off-shelf list lives in the realtime database, not in the menu the API returns, so it is
  // read here and handed down rather than derived from `categoryList`.
  const offShelfMap = useOffShelfDishes(user && user.restaurantId);

  const offShelfCount = useMemo(
    () => countOffShelf(categoryList, offShelfMap),
    [categoryList, offShelfMap]
  );

  // Each surviving category keeps its index in `categoryList`: dragging a category to reorder it
  // addresses the full list by position, so a position within the filtered list would move the wrong one.
  const visibleCategories = useMemo(() => {
    const filters = { query: dishSearch, offShelfOnly, offShelfMap };
    const isFiltering = dishSearch.trim() !== "" || offShelfOnly;

    return categoryList.reduce((kept, category, index) => {
      if (!isFiltering || hasVisibleItems(category, filters)) {
        kept.push({ category, index });
      }
      return kept;
    }, []);
  }, [categoryList, dishSearch, offShelfOnly, offShelfMap]);


  useEffect(() => {
    loadMenuCategories(menuId);
    loadMenuGroups(menuId);
    // `[props]` refetched both lists on every render; the menu is the only
    // thing that decides what to load.
  }, [menuId]);

  useEffect(() => {
    setCategoryList(categories);
  }, [categories]);

  useEffect(() => {
    setGroupList(groups);
  }, [groups]);

  const createNewCategory = () => {
    openNewCategory();
  };

  const moveCategory = useCallback(
    (dragIndex, hoverIndex) => {
      const dragCard = categoryList[dragIndex];
      const newSortedCategories = update(categoryList, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragCard],
        ],
      });
      setCategoryList(newSortedCategories);
      updateTheAllCategories({
        menuId,
        categories: newSortedCategories.map((category, index) => {
          return { ...category, sort: index };
        }),
      });
    },
    [categoryList]
  );

  const removeItemWithSlice = (items, index) => {
    let newSelectedOptions = [];

    if (index === 0) {
      newSelectedOptions = newSelectedOptions.concat(items.slice(1));
    } else if (index === items.length - 1) {
      newSelectedOptions = newSelectedOptions.concat(items.slice(0, -1));
    } else if (index > 0) {
      newSelectedOptions = newSelectedOptions.concat(
        items.slice(0, index),
        items.slice(index + 1)
      );
    }
    return newSelectedOptions;
  };

  const handleDrop = useCallback((index, item) => {
    console.log("handledrop", index, item);
  }, []);

  const removeGroupFromAllCategories = (groupId) => {
    console.log("removeGroupFromAllCategories", groupId, categories.length);
    let updateCategoryIds = new Set();
    let updateCategories = [];
    let newCategories = [];

    categories.forEach((category) => {
      let newItems = [];

      // console.log('category==', category.id);
  
      for (let item of category.items) {
        let newGroups = [];
        for (let group of (item.groups || [])) {
          //console.log('grouid==', group.id, item.id, category.id);
          if (group.id === groupId) {
            // console.log('grouid=======', group.id);
            updateCategoryIds.add(category.id);
          } else {
            newGroups.push(group);
          }
        }
        let newSizes = [];
        if (item.sizes) {
          for (let size of item.sizes) {
            let sizeGroups = [];
            if (size.groups) {
              for (let group of size.groups) {
                if (group.id === groupId) {
                  updateCategoryIds.add(category.id);
                } else {
                  sizeGroups.push(group);
                }
              }
            }
            newSizes.push({ ...size, groups: sizeGroups });
          }
        }

        let newItem = { ...item, groups: newGroups, sizes: newSizes };
        newItems.push(newItem);
      }
      // console.log("updateCategoryIds", updateCategoryIds);
      if (updateCategoryIds.has(category.id)) {
        const newCategory = { ...category, items: newItems };
        updateCategories.push(newCategory);
        newCategories.push(newCategory);
      } else {
        newCategories.push(category);
      }
    });
  // console.log("updateCategories", updateCategories);
    if (updateCategories.length > 0) {
      updateTheAllCategories({ menuId, categories: updateCategories });
      setCategoryList(newCategories);
    }
  };

  const handleGroupDelete = (groudId) => {
    // console.log("handleGroupDelete", groudId, menuId);
    const updatedItemIndex = groups.findIndex((item) => item.id === groudId);
    if (updatedItemIndex !== -1) {
      removeGroupFromAllCategories(groudId);
      deleteGroup(menuId, groudId);
    } else {
      console.log("group not found", groudId);
    }
  };

  const handleCategoryDelete = (categoryId) => {
    const updatedItemIndex = categories.findIndex(
      (item) => item.id === categoryId
    );
    if (updatedItemIndex !== -1) {
      const newCategories = removeItemWithSlice(categories, updatedItemIndex);
      try {
        deleteCategory(menuId, categoryId);
        updateNewCategories(newCategories);
      } catch (err) {
        console.log(err);
      }
      // setCategoryList(newCategories);
    }
  };

  return (
    <div className={classes.root}>
      <BackButton to={`/menu/${menuId}`} label="Menu" />
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Categories &amp; Dishes
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {categoryList.length}{" "}
            {categoryList.length === 1 ? "category" : "categories"} ·{" "}
            {dishCount} {dishCount === 1 ? "dish" : "dishes"}
            {offShelfCount > 0 ? ` · ${offShelfCount} off shelf` : ""}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button size="small" component={RouterLink} to={`/menuEditor/${menuId}`}>
            Try the new editor
          </Button>
        {isAdmin && (
          <Box display="flex" alignItems="center" gap={1}>
            {showIds && (
              <Typography variant="caption" color="textSecondary">
                Menu ID: {menuId}
              </Typography>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowIds((prev) => !prev)}
            >
              {showIds ? "Hide IDs" : "Show IDs"}
            </Button>
          </Box>
        )}
        </Box>
      </Box>
        <Grid container className={classes.panels}>
          <DndProvider backend={HTML5Backend}>
          <Grid item xs={12} sm={8} sx={{ flexDirection: "column" }} className={classes.panel}>
          <Box mb={2} flexShrink={0}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search dishes by name..."
                  value={dishSearch}
                  onChange={(e) => setDishSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: dishSearch ? (
                      <InputAdornment position="end">
                        <MuiIconButton size="small" onClick={() => setDishSearch("")}>
                          <ClearIcon fontSize="small" />
                        </MuiIconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <Box mt={1} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Tooltip title="Dishes customers cannot order right now: taken off or sold out on the POS, or hidden by their visibility settings">
                    <Chip
                      size="small"
                      icon={<VisibilityOffIcon fontSize="small" />}
                      label={`Off shelf${offShelfCount > 0 ? ` (${offShelfCount})` : ""}`}
                      color={offShelfOnly ? "primary" : "default"}
                      variant={offShelfOnly ? "filled" : "outlined"}
                      onClick={() => setOffShelfOnly((prev) => !prev)}
                      aria-pressed={offShelfOnly}
                    />
                  </Tooltip>
                  {offShelfOnly ? (
                    <Typography variant="caption" color="textSecondary">
                      Showing off-shelf dishes only
                    </Typography>
                  ) : null}
                </Box>
              </Box>
          <Box className={classes.panelScroll} role="region" aria-label="Categories & Dishes" tabIndex={0}>
                {visibleCategories.map(({ category, index }) => (
                  <TargetBox
                    accept={["box", "card"]}
                    dragType={"card"}
                    key={category.id}
                    index={index}
                    id={category.id}
                    onDrop={(item) => handleDrop(index, item)}
                    moveCard={moveCategory}
                  >
                    <Category
                      category={category}
                      key={category.id}
                      menuId={menuId}
                      deleteCategory={handleCategoryDelete}
                      searchQuery={dishSearch}
                      offShelfOnly={offShelfOnly}
                      offShelfMap={offShelfMap}
                      showIds={isAdmin && showIds}
                    />
                  </TargetBox>
                ))}

                {visibleCategories.length === 0 && offShelfOnly ? (
                  <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                    {dishSearch.trim()
                      ? "No off-shelf dish matches that search."
                      : "Every dish on this menu is on the shelf."}
                  </Typography>
                ) : null}

                {showNewCategory ? <NewCategory menuId={menuId} /> : null}

                <Box mt={1}>
                  <Button variant="contained" onClick={createNewCategory}>
                    Add Category
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid
             item xs={12} sm={4} sx={{ flexDirection: "column" }}
              className={`${classes.panel} ${classes.rightContainer}`}
            >
                  <Paper elevation={0} variant="outlined" className={classes.rightContent}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Choices &amp; Addons
                    </Typography>

                    <Box mt={1} mb={2} flexShrink={0}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search groups or options..."
                        value={groupSearch}
                        onChange={(e) => setGroupSearch(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: groupSearch ? (
                            <InputAdornment position="end">
                              <MuiIconButton size="small" onClick={() => setGroupSearch("")}>
                                <ClearIcon fontSize="small" />
                              </MuiIconButton>
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    </Box>

                    <Box className={classes.panelScroll} role="region" aria-label="Choices & Addons" tabIndex={0}>
                    <Grid container direction="column">
                      {groupList
                        .filter((group) => {
                          if (!groupSearch.trim()) return true;
                          const query = groupSearch.trim().toLowerCase();
                          const groupName = (group.name || "").toLowerCase();
                          if (groupName.includes(query)) return true;
                          return (group.options || []).some((option) => {
                            const optionName = (option.name || "").toLowerCase();
                            return optionName.includes(query);
                          });
                        })
                        // a boolean comparator coerces to 1/0 and never -1, so
                        // the panel was not actually sorted by `sort`
                        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
                        .map((group) => (
                        <SourceBox key={group.id} item={group}>
                          <Grid item key={group.id}>
                            <Group
                              group={group}
                              delete={handleGroupDelete}
                              menuId={menuId}
                              groups={groupList}
                            />
                          </Grid>
                        </SourceBox>
                      ))}
                    </Grid>

                    {showNewGroup ? <NewGroup menuId={menuId}  sort={groupList.length + 1} groups={groupList} /> : null}
                    <Button variant="contained" onClick={openNewGroup}>
                      Add New Group
                    </Button>
                    </Box>
                  </Paper>
            </Grid>
          </DndProvider>
        </Grid>
    
    </div>
  );
};

export default MenuCategroy;
